import { useState, useEffect, useContext, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import ConfirmDialog from "../components/ConfirmDialog";
import { EventContext } from "../../../../../contexts/EventContext";
import { MatchContext } from "../../../../../contexts/MatchContext";
import request from "../../../../../utilities/requests";
import { toast } from "../../../../../utilities/SnackBarUtils";

/*
  EventConfiguration

  This section contains the core configuration settings that determine
  how the event operates structurally. These are not simple toggles —
  they control pairing logic and questionnaire behavior.

  Components that will live here:
  - QuestionGroupSelect
  - PairingEventTypeSelect
*/
const pairingTypeHelp = {
  hetero: "Pairs will be made between He/Him and She/Her only",
  menseekingmen:
    "It is assumed that all attendees identify as He/Him and will be matched with each other",
  lesbian:
    "It is assumed that all attendees identify as She/Her and will be matched with each other",
  lgbtq:
    "Attendees may choose their pronouns and choose who they are interested in meeting",
  friend: "Pronouns are not considered",
  openspectrum: "Pronouns are not considered",
  dinner:
    "A hostless mixer-like event. Attendees can mark themselves as present. Checking in is not required. Attendees will see the Guest List",
};

const QuestionGroupSelect = ({ selectedGroup, questionGroups, onSelect }) => (
  <FormControl variant="outlined" sx={{minWidth: 200, mb: 2 }}>
    <InputLabel sx={{ top: -5 }}>Question Groups</InputLabel>
    <Select
      value={selectedGroup}
      label="Question Groups"
      onChange={(e) => onSelect(e.target.value)}
      sx={{ height: 45 }}
    >
      {questionGroups.map((option) => (
        <MenuItem key={option._id} value={option._id}>
          {option.name}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

const PairingEventTypeSelect = ({ pairingEventType, onChange }) => (
  <FormControl variant="outlined" sx={{minWidth: 200, mb: 2 }}>
    <InputLabel sx={{ top: -5 }}>Pairing Event Type</InputLabel>
    <Select
      value={pairingEventType || ""}
      label="Pairing Event Type"
      onChange={(e) => onChange(e.target.value)}
      sx={{ height: 45 }}
    >
      <MenuItem value="hetero">Hetero Speed Dating</MenuItem>
      <MenuItem value="menseekingmen">Men Seeking Men Speed Dating</MenuItem>
      <MenuItem value="lesbian">Lesbian Speed DAting</MenuItem>
      <MenuItem value="lgbtq">LGBTQ Speed Dating</MenuItem>
      <MenuItem value="friend">Speed Friending</MenuItem>
      <MenuItem value="openspectrum">Open Spectrum Speed Dating</MenuItem>
      <MenuItem value="mixer">Mixer</MenuItem>
      <MenuItem value="group">Group</MenuItem>
      <MenuItem value="dinner">Dinner</MenuItem>
    </Select>
    <Typography variant="caption" sx={{ p: 1 }}>
      {pairingTypeHelp[pairingEventType] || ""}
    </Typography>
  </FormControl>
);

const MixerGroupingSelect = ({
  showGrouping,
  selectedGrouping,
  groupings,
  onChange,
}) => {
  if (!showGrouping) return null;
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        width: 200,
        mb: 2,
      }}
    >
      <FormControl fullWidth variant="outlined">
        <InputLabel sx={{ top: -5 }}>Filter</InputLabel>
        <Select
          value={selectedGrouping}
          label="Mixer Filter"
          onChange={(e) => onChange(e.target.value)}
          sx={{ height: 45 }}
        >
          <MenuItem value="none">None</MenuItem>
          {groupings.map((option) => (
            <MenuItem key={option._id} value={option._id}>
              {option.systemLabel}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Typography variant="caption" sx={{ pl: 1 }}>
        The Mixer Filter acts as a filter on which attendees a user can see. It
        must be a 'related' question.
      </Typography>
    </Box>
  );
};

const EventConfiguration = ({ handleChange }) => {
  const { pairingEvent, setPairingEvent } = useContext(EventContext);
  const { warnings, setWarnings } = useContext(MatchContext);
  const [questionGroups, setQuestionGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [pairingEventType, setPairingEventType] = useState("");
  const [groupings, setGroupings] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const newGroup = useRef();
  const [showGrouping, setShowGrouping] = useState(false);
  const [selectedGrouping, setSelectedGrouping] = useState("null");

  useEffect(() => {
    if (!pairingEvent) return;

    getQuestionGroups();
    getMixerGroupings();
    setPairingEventType(pairingEvent?.pairingEventType);
     setSelectedGrouping(
        pairingEvent?.mixerGrouping === null
          ? "none"
          : pairingEvent?.mixerGrouping,
      );
  }, [pairingEvent]);

  useEffect(() => {
    if (["mixer", "group"].includes(pairingEventType)) {
      setShowGrouping(true);
      if (groupings.length === 0) {
        getMixerGroupings();
      }
    } else {
      setShowGrouping(false);
    }
  }, [pairingEventType]);

  const getQuestionGroups = async () => {
    const result = await request(
      `questiongroups?orgId=${pairingEvent?.event?.org}`,
    );
    if (result.success) {
      setQuestionGroups(result.questionGroups);
      const eResult = await request(
        `questiongroups/eventquestiongroup?eventId=${pairingEvent?.event?._id}`,
      );
      if (eResult.success && eResult?.eventQuestionGroup?.questionGroup) {
        setSelectedGroup(eResult.eventQuestionGroup.questionGroup);
        setWarnings(warnings.filter((w) => w.type !== "questionGroup"));
      }
    }
  };

  const getMixerGroupings = async () => {
    const result = await request(
      `pairingevent/mixergroupings?eventId=${pairingEvent.event._id}`,
    );
    if (result.success) {
      const g = result.groupings.filter((i) => i.questionType === "related");
      setGroupings(g);
    }
  };

  const handleConfirm = (questionGroupId) => {
    newGroup.current = questionGroupId;
    if (selectedGroup === "") {
      updateEventQuestionGroup();
      return;
    }
    setConfirmOpen(true);
  };

  const updateEventQuestionGroup = async () => {
    const body = {
      eventId: pairingEvent?.event?._id,
      questionGroupId: newGroup.current,
    };
    const result = await request("questiongroups/assign", "post", body);
    if (result.success) {
      setSelectedGroup(newGroup.current);
      setWarnings(warnings.filter((w) => w.type !== "questionGroup"));
      toast.success("Question group assigned to event successfully!");
      const result = await request(
        `pairingevent?eventId=${pairingEvent.event._id}`,
      );
      if (result.success) {
        setPairingEvent(result.pairingEvents);
      }
    }
    setConfirmOpen(false);
  };

  const handlePairingEventTypeChange = async (val) => {
    const body = {
      pairingEventId: pairingEvent._id,
      update: { pairingEventType: val },
    };
    try {
      const result = await request("pairingevent/update", "post", body);
      if (result.success) {
        setPairingEventType(val);
        setWarnings(warnings.filter((w) => w.type !== "pairingEventType"));
        if (val === "dinner") {
          handleChange("trackIsPresent", true);
        } else if (val === "group") {
          handleChange("autoFillGroups", true);
        }
        if (["mixer", "dinner"].includes(val)) {
          handleChange("autoCompleteMode", "time");
        }
        toast.success("Pairing event type updated successfully");
        const result = await request(
          `pairingevent?eventId=${pairingEvent.event._id}`,
        );
        if (result.success) {
          setPairingEvent(result.pairingEvents);
        }
      }
    } catch (e) {
      toast.error(`Error updating pairing event type. Error: ${e.message}`);
    }
  };

  const handleGroupingChange = async (val) => {
    const body = {
      eventId: pairingEvent.event._id,
      questionId: val,
    };
    const result = await request("pairingevent/setmixergrouping", "post", body);
    if (result.success) {
      setSelectedGrouping(val);
      toast.success("Mixer grouping updated");
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="subtitle1" sx={{ mb: 2 }}>
        Event Configuration
      </Typography>

      <Typography variant="caption" sx={{ display: "block", mb: 3 }}>
        These settings control how attendees are paired and how profile
        questions are used during the event.
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <QuestionGroupSelect
            selectedGroup={selectedGroup}
            questionGroups={questionGroups}
            onSelect={handleConfirm}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <PairingEventTypeSelect
            pairingEventType={pairingEventType}
            onChange={handlePairingEventTypeChange}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <MixerGroupingSelect
            showGrouping={showGrouping}
            selectedGrouping={selectedGrouping}
            groupings={groupings}
            onChange={handleGroupingChange}
          />
        </Grid>
      </Grid>
      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={updateEventQuestionGroup}
      />
    </Paper>
  );
};

export default EventConfiguration;
